import axios from 'axios';
import { REACT_APP_SIGN_OUT_REDIRECT_URL } from 'runtimeEnvVars';

import { TRACKING_VISITOR_KEY } from 'utils/constants';
import { saveIntendedDestination } from 'utils/intendedDestination';
import { getSessionItem } from 'utils/storageUtils';

import {
  DEFAULT_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
} from '../containers/FlashMessage/constants';

const SIGN_OUT_URL = REACT_APP_SIGN_OUT_REDIRECT_URL;
let cancelToken;

function httpStatusTextOrFallback(errorResponse) {
  const s = (errorResponse.statusText || '').trim();
  if (s) {
    return s;
  }
  if (errorResponse.status === 404) {
    return 'Not found';
  }
  if (errorResponse.status === 403) {
    return 'Forbidden';
  }
  return `Error ${errorResponse.status}`;
}

/**
 * Maps API `error` / `errors` and similar fields to a single user-visible
 * string. Handles the three shapes Rails endpoints return: a bare string, a
 * message array, and a per-field hash (`{ field => message }` or
 * `{ field => [messages] }` — what `errors.messages` and hand-rolled
 * validators such as ReviewQuestionnaire::Validator produce). Anything with no
 * readable text left in it yields null so the caller can fall back to the next
 * field or the default message.
 * @param {unknown} payload
 * @returns {string | null}
 */
export function apiErrorPayloadToString(payload) {
  if (payload == null) {
    return null;
  }
  if (typeof payload === 'string') {
    const t = payload.trim();
    return t.length ? t : null;
  }
  if (Array.isArray(payload)) {
    if (payload.length === 0) {
      return null;
    }
    const parts = payload
      .map(item => {
        if (typeof item === 'string') {
          return item.trim();
        }
        if (item != null && typeof item === 'object' && 'message' in item) {
          return String(
            /** @type {{ message?: unknown }} */ (item).message,
          ).trim();
        }
        return String(item).trim();
      })
      .filter(part => part.length > 0);
    return parts.length ? parts.join('. ') : null;
  }
  if (typeof payload === 'object') {
    // Field-error hash. Values recurse, so a hash of strings and a hash of
    // message arrays both resolve; non-text values (numbers, nested nulls)
    // drop out rather than reaching the user as "[object Object]".
    const parts = Object.values(payload)
      .map(value => apiErrorPayloadToString(value))
      .filter(part => part != null);
    return parts.length ? parts.join('. ') : null;
  }
  return null;
}

/**
 * @param {Record<string, unknown>} data
 * @param {import('axios').AxiosResponse} errorResponse
 * @param {boolean} is403or404
 * @returns {string | null}
 */
function messageFromErrorResponseBody(data, errorResponse, is403or404) {
  const fields = [
    data.error,
    data.errors,
    data.error_message,
    data.error_type,
    data.message,
  ];
  for (let i = 0; i < fields.length; i += 1) {
    const msg = apiErrorPayloadToString(fields[i]);
    if (msg) {
      return msg;
    }
  }
  if (is403or404) {
    return httpStatusTextOrFallback(errorResponse);
  }
  return null;
}

axios.interceptors.response.use(null, error => {
  if (error && error.response) {
    if (error.response.status === 401) {
      saveIntendedDestination();
      const visitorTrack = getSessionItem(TRACKING_VISITOR_KEY);
      window.location.replace(`${SIGN_OUT_URL}?app=${visitorTrack}`);
    }
    const { data, status } = error.response;
    if (status >= 500) {
      return Promise.reject(DEFAULT_ERROR_MESSAGE);
    }
    const is403or404 = status === 403 || status === 404;
    if (data != null && typeof data === 'object' && !Array.isArray(data)) {
      const message =
        messageFromErrorResponseBody(data, error.response, is403or404) ??
        DEFAULT_ERROR_MESSAGE;
      return Promise.reject(message);
    }
    if (typeof data === 'string' && is403or404 && data.trim().length) {
      const withoutTags = data.replace(/<[^>]+>/g, ' ').trim();
      if (withoutTags) {
        return Promise.reject(
          withoutTags.length > 240
            ? `${withoutTags.slice(0, 240)}…`
            : withoutTags,
        );
      }
    }
    if (is403or404) {
      return Promise.reject(httpStatusTextOrFallback(error.response));
    }
    return Promise.reject(DEFAULT_ERROR_MESSAGE);
  }
  if (axios.isCancel(error)) {
    return Promise.reject(error);
  }
  return Promise.reject(NETWORK_ERROR_MESSAGE);
});

const config = {
  withCredentials: true,
};

export function apiGet(path, params) {
  return axios.get(path, { ...config, params });
}
export function apiPost(path, data) {
  return axios.post(path, data, config);
}

export function apiPatch(path, data) {
  return axios.patch(path, data, config);
}

export function apiPut(path, data) {
  return axios.put(path, data, config);
}

export function apiDelete(path, data) {
  return axios.delete(path, { ...config, ...data });
}

export function apiGetWithCancelRequest(path, params) {
  if (cancelToken) {
    cancelToken.cancel('request canceled');
  }
  cancelToken = axios.CancelToken.source();
  return axios.get(path, {
    ...config,
    params,
    ...{ cancelToken: cancelToken.token },
  });
}

/** Cancels the in-flight `apiGetWithCancelRequest` (e.g. messenger messages) without starting a new request. */
export function cancelPendingApiGetWithCancelRequest() {
  if (cancelToken) {
    cancelToken.cancel('request canceled');
  }
}
